import { SearchX } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../actions/button";
import { SortableDataTable } from "../components/arc/sortable-data-table/sortable-data-table";
import { PageEmpty, PageError, PageLoading } from "../shell/page";
import styles from "./list.module.css";

export interface ListColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  numeric?: boolean;
  width?: number | string;
  /** Sorting is within the rows loaded so far (Arc sorts client-side); leave it off for server-ordered lists. */
  sortable?: boolean;
}

export interface ListViewProps<T extends Record<string, unknown>> {
  rows: T[];
  columns: ListColumn<T>[];
  rowKey: (row: T) => string;
  /** The table's accessible name, e.g. "Orders". */
  caption: string;
  itemName: { one: string; other: string };
  status: "pending" | "error" | "success";
  errorMessage?: string;
  onRetry?: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Filters are applied: an empty result says so and offers to reset them. */
  filtered: boolean;
  onResetFilters?: () => void;
  /** What an empty, unfiltered list says: nothing exists yet, and what to do about it. */
  empty: { title: string; description: string; action?: ReactNode };
  /** Filters and actions above the table. */
  toolbar?: ReactNode;
}

/**
 * A page's list: toolbar, table, "Load more", and every state in place of the table (loading,
 * error with retry, nothing yet, nothing matching). Lists are cursor-paginated without totals,
 * so it says how many are shown, never "page 2 of 9".
 */
export function ListView<T extends Record<string, unknown>>(props: ListViewProps<T>) {
  const { rows, columns, rowKey, caption, itemName, status, hasMore, loadingMore, onLoadMore } = props;
  return (
    <div className={styles.list}>
      {props.toolbar ? <div className={styles.toolbar}>{props.toolbar}</div> : null}
      {status === "pending" ? (
        <PageLoading label={`Loading ${itemName.other}`} />
      ) : status === "error" ? (
        <PageError message={props.errorMessage ?? `The ${itemName.other} didn't load.`} onRetry={props.onRetry} />
      ) : rows.length === 0 ? (
        props.filtered ? (
          <PageEmpty
            icon={<SearchX />}
            title={`No ${itemName.other} match these filters`}
            description="Change or clear the filters to see more."
            action={
              props.onResetFilters ? (
                <Button variant="secondary" size="sm" onClick={props.onResetFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <PageEmpty title={props.empty.title} description={props.empty.description} action={props.empty.action} />
        )
      ) : (
        <>
          <SortableDataTable
            rows={rows}
            rowKey={rowKey}
            caption={caption}
            itemName={itemName}
            columns={columns.map((c) => ({
              key: c.key,
              label: c.label,
              numeric: c.numeric,
              width: c.width,
              sortable: c.sortable ?? false,
              render: c.render ? (_value: unknown, row: T) => c.render?.(row) : undefined,
            }))}
          />
          <LoadMore
            shown={rows.length}
            itemName={itemName}
            hasMore={hasMore}
            loading={loadingMore}
            onLoadMore={onLoadMore}
          />
        </>
      )}
    </div>
  );
}

/** How many are shown, and the next page while there is one. */
export function LoadMore({
  shown,
  itemName,
  hasMore,
  loading,
  onLoadMore,
}: {
  shown: number;
  itemName: { one: string; other: string };
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
}) {
  return (
    <div className={styles.more}>
      <span className={styles.count}>
        {shown} {shown === 1 ? itemName.one : itemName.other}
        {hasMore ? " so far" : ""}
      </span>
      {hasMore ? (
        <Button variant="secondary" size="sm" loading={loading} onClick={onLoadMore}>
          Load more
        </Button>
      ) : null}
    </div>
  );
}
